import { useToast } from '@lifebit-ai/lds'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { extractErrorMessage } from 'core/utils'
import { approveDataAccessRequest } from 'services/data-access-requests'
import { DataAccessRequestQueryKeys } from 'services/data-access-requests/queryKeys'
import type { ApproveDataAccessRequestParams } from 'services/data-access-requests/types'
import { invalidateRequestActivityQueries } from 'services/request-activity/invalidateRequestActivityQueries'
import type { ApiErrorResponse } from 'services/types'
import { isAirlockRequestsV3RoutesEnabled } from 'utils/env'

interface UseApproveDataAccessRequestParams {
  onSuccess?: () => void
  requestId: string
}

export const useApproveDataAccessRequest = ({ onSuccess, requestId }: UseApproveDataAccessRequestParams) => {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation<void, ApiErrorResponse, ApproveDataAccessRequestParams | undefined>({
    mutationFn: params => {
      if (!isAirlockRequestsV3RoutesEnabled()) {
        return Promise.resolve()
      }

      return approveDataAccessRequest(requestId, params)
    },
    onSuccess: () => {
      toast({ message: 'Data access request approved successfully' })
      queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.Verification(requestId) })
      queryClient.invalidateQueries({ queryKey: DataAccessRequestQueryKeys.All() })
      invalidateRequestActivityQueries(queryClient)
      onSuccess?.()
    },
    onError: error => {
      toast({ message: extractErrorMessage(error, 'Failed to approve data access request') })
    }
  })
}
